import type { Container } from "pixi.js";
import {
  AsciiFilter,
  CRTFilter,
  GlitchFilter,
  GlowFilter,
  HslAdjustmentFilter,
  PixelateFilter,
} from "pixi-filters";

import type { IOverlayShaderPreset } from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Overlay.interface";

import type { OverlayFrameUpdater } from "./OverlayElementAnimator";

/**
 * Aplica un shader preset (filtro Pixi) al container del elemento y devuelve un
 * updater per-frame para los presets animados (holographic / scanlines /
 * glitch), o null para los estáticos. `intensity` 0–100 lo interpreta cada
 * preset. Espejo Pixi de las cadenas de filtros CSS del DOM.
 */
export const applyShaderPreset = (
  container: Container,
  preset: IOverlayShaderPreset,
): OverlayFrameUpdater | null => {
  const intensity = Math.max(0, Math.min(1, (preset.intensity ?? 50) / 100));

  switch (preset.kind) {
    case "neon": {
      const f = new GlowFilter({
        distance: 16,
        outerStrength: 1 + intensity * 6,
        innerStrength: 0,
        color: 0xffffff,
        quality: 0.3,
      });
      container.filters = [f];
      return null;
    }
    case "pixelate": {
      const size = Math.max(1, Math.round(2 + intensity * 18));
      container.filters = [new PixelateFilter(size)];
      return null;
    }
    case "ascii": {
      const size = Math.max(4, Math.round(6 + intensity * 14));
      container.filters = [new AsciiFilter({ size })];
      return null;
    }
    case "holographic": {
      const f = new HslAdjustmentFilter({
        hue: 0,
        saturation: 0.3 + intensity * 0.5,
        lightness: 0,
        colorize: false,
        alpha: 1,
      });
      container.filters = [f];
      // Rota el matiz continuamente → barrido holográfico.
      const speed = 0.04 * (0.5 + intensity);
      return (elapsedMs: number) => {
        f.hue = (elapsedMs * speed) % 360;
      };
    }
    case "scanlines": {
      const f = new CRTFilter({
        lineWidth: 1 + intensity * 3,
        lineContrast: 0.2 + intensity * 0.3,
        vignetting: 0.1,
        noise: 0.05 + intensity * 0.1,
      });
      container.filters = [f];
      return (elapsedMs: number) => {
        f.time = elapsedMs / 1000;
      };
    }
    case "glitch": {
      const f = new GlitchFilter({
        slices: 5 + Math.round(intensity * 10),
        offset: 10 + intensity * 90,
      });
      container.filters = [f];
      // Re-baraja el desplazamiento ~cada 90ms (no por frame, para que no sea
      // ruido puro). Math.random es válido en código de app.
      let lastTick = -1;
      return (elapsedMs: number) => {
        const tick = Math.floor(elapsedMs / 90);
        if (tick !== lastTick) {
          lastTick = tick;
          f.seed = Math.random();
        }
      };
    }
    default:
      return null;
  }
};
